// eslint-disable-next-line
import React, { ComponentType } from 'react';
import NoSsr from '@material-ui/core/NoSsr';
import {
  createMuiTheme,
  ThemeProvider as MuiThemeProvider,
  StylesProvider,
} from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider } from 'styled-components';

export const colors = {
  lightRed: 'rgb(242, 222, 222)',
  darkRed: 'rgb(183, 39, 29)',
  red60: 'rgb(183,32,46)',
  red: 'rgb(239, 56, 41)',
  lighterGreen: 'rgba(242, 250, 239, 1)',
  lightGreen: 'rgba(212, 237, 218, 1)',
  green: 'rgb(103, 167, 77)',
  white: 'rgb(255, 255, 255)',
  lighterGrey: 'rgb(238, 238, 238)', // 20
  lightGrey: 'rgb(216, 216, 216)', // 40
  grey: 'rgb(133, 133, 133)', // 60
  darkGrey: 'rgb(51,51,51)', // 80
  black: 'rgb(0,0,0)',
  lightYellow: 'rgb(255, 243, 205)',
  yellow: 'rgba(250, 180, 0, 1)',
  darkYellow: 'rgb(133, 100, 4)',
  lightBlue: 'rgb(222, 240, 243)',
  blue: 'rgb(25,118,210)',
  darkBlue: 'rgb(12, 84, 96)',
};

export const wfmTheme = createMuiTheme({
  palette: {
    primary: {
      main: colors.red,
    },
    secondary: {
      main: colors.black,
    },
    error: {
      light: colors.lightRed,
      main: colors.red60,
    },
    info: { light: colors.lightBlue, main: colors.blue, dark: colors.darkBlue },
    warning: {
      light: colors.lightYellow,
      main: colors.yellow,
      dark: colors.darkYellow,
    },
    success: { light: colors.lightGreen, main: colors.green },
  },
  typography: {
    h5: {
      fontSize: '1.625rem', // 26
    },
    h6: {
      fontSize: '1.25rem', // 20
    },
    subtitle1: {
      fontSize: '0.937rem', // 15
    },
    body1: {
      fontSize: '0.937rem', // 15
    },
    body2: {
      fontSize: '0.8125rem', // 13
    },
  },
  overrides: {
    MuiTooltip: {
      tooltip: {
        maxWidth: 'auto',
        fontWeight: 400,
        fontSize: '0.937rem', // 15
        padding: '.5rem',
      },
    },
    MuiFormLabel: {
      // change the color of the label of inputs on focus
      root: {
        fontSize: '1.0625rem', // 17
        '&$focused': {
          color: colors.grey,
        },
      },
    },
    // MuiNativeSelect: {
    //   // IE style fixes
    //   root: {
    //     '&$disabled': {
    //       '&::-ms-value': {
    //         color: `${colors.selectPlaceholder}`,
    //       },
    //     },
    //   },
    // },
    MuiInput: {
      // IE style fixes
      root: {
        overflow: 'hidden',
      },
      input: {
        '&::-ms-clear': {
          display: 'none',
        },
        // remove chrome autofill background
        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
          '-webkit-box-shadow': '0 0 0 30px white inset !important',
        },
      },
      // change the color of the bottom line of inputs on focus
      underline: {
        '&:after': {
          borderBottom: colors.grey,
        },
        '&$disabled:before': {
          borderBottomStyle: 'solid',
          borderBottomColor: colors.lightGrey,
        },
      },
    },
    MuiInputBase: {
      input: { fontSize: '0.937rem' },
    },
    MuiButton: {
      root: {
        textTransform: 'none',
      },
    },
    MuiDialogContent: {
      root: {
        '&::-webkit-scrollbar': {
          width: 10,
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0, 0, 0, 0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.3)',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiSnackbarContent: {
      root: {
        padding: '12px 36px',
      },
    },
    MuiChip: {
      label: { fontSize: '0.937rem' },
      sizeSmall: {
        fontWeight: 500,
      },
    },
    MuiIconButton: {
      root: {
        padding: '6px',
      },
    },
  },
});

const themeColors = wfmTheme;

export function withWfmTheme<P>(Component: ComponentType<P>) {
  function WithMuiTheme(props: P) {
    // MuiThemeProvider makes the theme available down the React tree
    // thanks to React context.
    return (
      <NoSsr>
        <StylesProvider injectFirst>
          <MuiThemeProvider theme={wfmTheme}>
            <ThemeProvider theme={themeColors}>
              {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
              <CssBaseline />
              <Component {...props} />
            </ThemeProvider>
          </MuiThemeProvider>
        </StylesProvider>
      </NoSsr>
    );
  }

  return WithMuiTheme;
}
